import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, useAuth, axiosClient } from './auth-lib';
import { ToastProvider, AppSplash } from './design-system';
import { NotificationProvider } from './context/NotificationContext';
import { SHOW_APP_SPLASH } from './config/loaders';
import Login from './pages/Login';
import Register from './pages/Register';
import SubscriptionOnboarding from './pages/SubscriptionOnboarding';
import AdminSubscriptions from './admin/pages/AdminSubscriptions';
import ForgotPassword from './pages/ForgotPassword';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminOrganizations from './admin/pages/AdminOrganizations';
import AdminServices from './admin/pages/AdminServices';
import AdminPortals from './admin/pages/AdminPortals';
import AdminIntegrations from './admin/pages/AdminIntegrations';
import AdminLanding from './admin/pages/AdminLanding';
import AdminAuditLogs from './admin/pages/AdminAuditLogs';
import CreateEditOrganization from './admin/pages/CreateEditOrganization';
import OrganizationDetail from './admin/pages/OrganizationDetail';
import OrgAdminDashboard from './org-admin/pages/OrgAdminDashboard';
import OrgAdminUsers from './org-admin/pages/OrgAdminUsers';
import OrgAdminUserProfile from './org-admin/pages/OrgAdminUserProfile';
import OrgAdminServices from './org-admin/pages/OrgAdminServices';
import OrgAdminSettings from './org-admin/pages/OrgAdminSettings';
import OrgAdminSettingsDetail from './org-admin/pages/OrgAdminSettingsDetail';
import OrgAdminTasks from './org-admin/pages/OrgAdminTasks';
import OrgAdminAi from './org-admin/pages/OrgAdminAi';
import OrgAdminCreateTask from './org-admin/pages/OrgAdminCreateTask';
import OrgAdminTaskDetail from './org-admin/pages/OrgAdminTaskDetail';
import OrgAdminDeletedTasks from './org-admin/pages/OrgAdminDeletedTasks';
import OrgAdminClients from './org-admin/pages/OrgAdminClients';
import CreateEditClient from './org-admin/pages/CreateEditClient';
import OrgAdminCredentials from './org-admin/pages/OrgAdminCredentials';
import OrgAdminReports from './org-admin/pages/OrgAdminReports';
import OrgAdminAuditLogs from './org-admin/pages/OrgAdminAuditLogs';
import OrgAdminDocuments from './org-admin/pages/OrgAdminDocuments';
import ClientDetail from './org-admin/pages/ClientDetail';
import ApprovalQueue from './org-admin/pages/ApprovalQueue';
import ComplianceDashboard from './org-admin/pages/ComplianceDashboard';
import OrgAdminTodos from './org-admin/pages/OrgAdminTodos';
import OrgAdminPermissions from './org-admin/pages/OrgAdminPermissions';
import OrgAdminNotices from './org-admin/pages/OrgAdminNotices';
import OrgAdminNotes from './org-admin/pages/OrgAdminNotes';
import OrgAdminDSC from './org-admin/pages/OrgAdminDSC';
import OrgAdminPhysicalFiles from './org-admin/pages/OrgAdminPhysicalFiles';
import OrgAdminClientRequests from './org-admin/pages/OrgAdminClientRequests';
import OrgAdminClientRequestDetail from './org-admin/pages/OrgAdminClientRequestDetail';
import OrgAdminCreateClientRequest from './org-admin/pages/OrgAdminCreateClientRequest';
import OrgAdminChat from './org-admin/pages/OrgAdminChat';
import OrgAdminAttendance from './org-admin/pages/OrgAdminAttendance';
import OrgAdminFinance from './org-admin/pages/OrgAdminFinance';
import OrgAdminRenewals from './org-admin/pages/OrgAdminRenewals';
import OrgAdminRetainers from './org-admin/pages/OrgAdminRetainers';
import OrgAdminBroadcast from './org-admin/pages/OrgAdminBroadcast';
import Notifications from './pages/Notifications';
import OrgAdminTally from './org-admin/pages/OrgAdminTally';
import StyleGuide from './pages/StyleGuide';
import NotFound from './pages/NotFound';
import Landing from './pages/Landing';
// CLIENT PORTAL — an entirely separate audience with its own auth provider and
// its own token storage. Mounted under /portal/* and sharing nothing with the
// staff session above except the design-system components.
import { PortalAuthProvider, PortalRoute } from './portal/PortalAuthContext';
import PortalLogin from './portal/PortalLogin';
import PortalSetPassword from './portal/PortalSetPassword';
import PortalHome from './portal/PortalHome';
import PortalRequests from './portal/PortalRequests';
import PortalRequestDetail from './portal/PortalRequestDetail';
import PortalDocuments from './portal/PortalDocuments';
import PortalCompliance from './portal/PortalCompliance';
import PortalBilling from './portal/PortalBilling';
import PortalInvoiceDetail from './portal/PortalInvoiceDetail';
import PortalChat from './portal/PortalChat';
// Employee area — merged in from the former standalone employee portal. Staff
// below Partner level land here after login (see Login.jsx).
import EmployeeHome from './employee/pages/EmployeeHome';
import EmployeeTasks from './employee/pages/EmployeeTasks';
import EmployeeTodos from './employee/pages/EmployeeTodos';
import EmployeeProfile from './employee/pages/EmployeeProfile';
import EmployeeTaskDetail from './employee/pages/TaskDetail';
import EmployeeCreateTask from './employee/pages/CreateTask';
import EmployeeCompliance from './employee/pages/ComplianceWorkspace';
import EmployeeChat from './employee/pages/EmployeeChat';
import EmployeeAttendance from './employee/pages/EmployeeAttendance';
import './App.css';

// Super Admin Route Guard - Only Super Admin can access /admin routes
function SuperAdminRoute({ children }) {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.isSuperAdmin) {
    console.warn('Access denied: Only Super Admin can access this route');
    return <Navigate to="/org-admin" replace />;
  }

  return children;
}

// ─── No approved subscription, no app ───────────────────────────────────────
//
// A firm that has registered but not been approved gets the plans screen
// instead of the cockpit.
//
// ⚠️ This is a REDIRECT, not the enforcement. Both APIs answer 402 to an
// unapproved firm whatever the browser does (auth-service's
// middleware/requireActiveSubscription.js and hrms's middleware/
// subscriptionGate.js). This exists so the first thing a new firm sees is the
// plans, rather than a dashboard full of failed requests.
//
// Asked once per mount rather than held in a context: the answer changes rarely,
// and the axios interceptor already catches every later change mid-session.
function SubscriptionRoute({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState('checking');

  useEffect(() => {
    // A super admin belongs to no firm and has nothing to subscribe to.
    if (user?.isSuperAdmin) { setState('ok'); return undefined; }
    let alive = true;
    axiosClient
      .get('/subscription')
      .then((res) => { if (alive) setState(res.data?.subscription?.isActive ? 'ok' : 'inactive'); })
      // ⚠️ A network failure is NOT proof the firm is unsubscribed. Locking
      // everyone out because the auth service blinked would be worse than what
      // this guards against — and the APIs still refuse an unapproved firm
      // regardless, so failing open here costs nothing.
      .catch(() => { if (alive) setState('ok'); });
    return () => { alive = false; };
  }, [user]);

  if (state === 'checking') return SHOW_APP_SPLASH ? <AppSplash /> : null;
  if (state === 'inactive') return <Navigate to="/subscription" replace />;
  return children;
}

// Every route in the staff app: signed in AND the firm approved.
//
// Composed once and used everywhere rather than nesting the two guards at each
// of fifty route declarations — one of the fifty would eventually be written
// with only the first, and it would be the one that mattered. Props (the
// occasional `requiredPermission`) pass straight through to ProtectedRoute.
//
// ⚠️ /subscription is the ONE route that must NOT use this: it is where this
// guard SENDS an unapproved firm, so requiring a subscription there is a loop.
// It uses ProtectedRoute directly and sits outside the cockpit tree entirely.
function AppRoute({ children, ...props }) {
  return (
    <ProtectedRoute {...props}>
      <SubscriptionRoute>{children}</SubscriptionRoute>
    </ProtectedRoute>
  );
}

// Show the brand splash while the auth session is being verified on boot, so
// the whole app comes up behind one loader instead of flashing bare routes.
function BootGate({ children }) {
  const { loading } = useAuth();
  if (SHOW_APP_SPLASH && loading) return <AppSplash />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider authServiceUrl={process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:4000'}>
      <ToastProvider>
       {/* Inside BrowserRouter (it navigates on a notification click) and inside
           AuthProvider (it opens a staff socket and fetches as the current
           user). It wraps the login routes too, which is harmless: with no user
           it fetches nothing and opens no socket. */}
       <NotificationProvider>
       <BootGate>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/style-guide" element={<StyleGuide />} />

          {/* Where a firm without an approved plan lands. ⚠️ ProtectedRoute,
              NOT AppRoute — it is where the subscription gate SENDS people, so
              gating it on having a subscription is a loop. Deliberately outside
              the cockpit tree: it needs no nav permission and renders no
              sidebar, so no exemption has to be carved anywhere else. */}
          <Route
            path="/subscription"
            element={<ProtectedRoute><SubscriptionOnboarding /></ProtectedRoute>}
          />

          {/* Client portal. Deliberately declared OUTSIDE the staff
              ProtectedRoute tree — PortalRoute is its gate, and a staff session
              grants no access here (nor a client session to anything else). */}
          <Route
            path="/portal/*"
            element={
              <PortalAuthProvider>
                <Routes>
                  <Route path="login" element={<PortalLogin />} />
                  <Route path="set-password" element={<PortalRoute><PortalSetPassword /></PortalRoute>} />
                  <Route index element={<PortalRoute><PortalHome /></PortalRoute>} />
                  <Route path="requests" element={<PortalRoute><PortalRequests /></PortalRoute>} />
                  <Route path="requests/:id" element={<PortalRoute><PortalRequestDetail /></PortalRoute>} />
                  <Route path="documents" element={<PortalRoute><PortalDocuments /></PortalRoute>} />
                  <Route path="compliance" element={<PortalRoute><PortalCompliance /></PortalRoute>} />
                  <Route path="billing" element={<PortalRoute><PortalBilling /></PortalRoute>} />
                  <Route path="billing/:id" element={<PortalRoute><PortalInvoiceDetail /></PortalRoute>} />
                  <Route path="chat" element={<PortalRoute><PortalChat /></PortalRoute>} />
                  {/* A 404, not a silent redirect to /portal. Bouncing a
                      client to their dashboard hides the fact that the link
                      they followed is dead — they conclude the app ignored
                      them. `scope="portal"` keeps every route out of this
                      page pointing back INTO the portal: a client must never
                      be offered the staff cockpit or a staff sign-in. */}
                  <Route path="*" element={<NotFound scope="portal" />} />
                </Routes>
              </PortalAuthProvider>
            }
          />
          {/* `/` is the PUBLIC marketing page, not a redirect any more.
              Landing itself decides: a signed-in visitor is sent straight to
              /org-admin (somebody with a session wants their work, not the
              pitch), and when nothing is published it falls through to /login —
              which is exactly what this route did before. So an unpublished
              page changes nothing about how the app behaves. */}
          <Route path="/" element={<Landing />} />

          {/* Super Admin Only Routes */}
          <Route
            path="/admin"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminDashboard /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminOrganizations /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminSubscriptions /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/organizations/create"
            element={
              <AppRoute>
                <SuperAdminRoute><CreateEditOrganization /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/organizations/:id"
            element={
              <AppRoute>
                <SuperAdminRoute><OrganizationDetail /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/organizations/:id/edit"
            element={
              <AppRoute>
                <SuperAdminRoute><CreateEditOrganization /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/services"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminServices /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/portals"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminPortals /></SuperAdminRoute>
              </AppRoute>
            }
          />
          {/* The bearer keys other applications read this platform's client
              records with. Super admin only: a key may span organizations, and
              the page lists every firm's name to scope one. */}
          <Route
            path="/admin/integrations"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminIntegrations /></SuperAdminRoute>
              </AppRoute>
            }
          />
          {/* The editor for the PUBLIC page at `/`. Super admin only — the
              landing page is platform marketing, not a per-firm setting. */}
          <Route
            path="/admin/landing"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminLanding /></SuperAdminRoute>
              </AppRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <AppRoute>
                <SuperAdminRoute><AdminAuditLogs /></SuperAdminRoute>
              </AppRoute>
            }
          />

          {/* Organization Admin Routes */}
          <Route
            path="/org-admin"
            element={<AppRoute><OrgAdminDashboard /></AppRoute>}
          />
          <Route
            path="/org-admin/users"
            element={<AppRoute><OrgAdminUsers /></AppRoute>}
          />
          <Route
            path="/org-admin/users/:id"
            element={<AppRoute><OrgAdminUserProfile /></AppRoute>}
          />
          <Route
            path="/org-admin/services"
            element={<AppRoute><OrgAdminServices /></AppRoute>}
          />
          <Route
            path="/org-admin/settings"
            element={<AppRoute><OrgAdminSettings /></AppRoute>}
          />
          {/* One screen per setting. `requiredNavKeyFor` matches on the path
              prefix, so every section inherits nav.settings — a hidden Settings
              menu keeps these unreachable by URL too. */}
          <Route
            path="/org-admin/settings/:section"
            element={<AppRoute><OrgAdminSettingsDetail /></AppRoute>}
          />
          <Route
            path="/org-admin/tasks"
            element={<AppRoute><OrgAdminTasks /></AppRoute>}
          />
          {/* The command bar. Behind the same AppRoute wrapper as every other
              cockpit page, so the subscription gate and the auth guard apply
              unchanged — a route that creates tasks must not be an exemption. */}
          {/* The AI section: smart search over the whole practice, plus the
              command bar. Top-level rather than a Tasks child — it searches
              eleven kinds of record, not just tasks. */}
          <Route
            path="/org-admin/ai"
            element={<AppRoute><OrgAdminAi /></AppRoute>}
          />
          {/* The old path, kept so a bookmark or an old link still lands
              somewhere rather than on the 404 page. */}
          <Route path="/org-admin/command" element={<Navigate to="/org-admin/ai" replace />} />
          <Route
            path="/org-admin/todos"
            element={<AppRoute><OrgAdminTodos /></AppRoute>}
          />
          <Route
            path="/org-admin/tasks/create"
            element={<AppRoute><OrgAdminCreateTask /></AppRoute>}
          />
          <Route
            path="/org-admin/tasks/deleted"
            element={<AppRoute><OrgAdminDeletedTasks /></AppRoute>}
          />
          <Route
            path="/org-admin/tasks/:id"
            element={<AppRoute><OrgAdminTaskDetail /></AppRoute>}
          />
          <Route
            path="/org-admin/clients"
            element={<AppRoute><OrgAdminClients /></AppRoute>}
          />
          {/* Client requests — the firm's half of the portal conversation.
              'new' precedes '/:id' so it isn't parsed as a request id. */}
          <Route
            path="/org-admin/chat"
            element={<AppRoute><OrgAdminChat /></AppRoute>}
          />
          <Route
            path="/org-admin/attendance"
            element={<AppRoute><OrgAdminAttendance /></AppRoute>}
          />
          <Route
            path="/org-admin/finance"
            element={<AppRoute><OrgAdminFinance /></AppRoute>}
          />
          <Route
            path="/org-admin/renewals"
            element={<AppRoute><OrgAdminRenewals /></AppRoute>}
          />
          {/* Standing arrangements with clients: the services covered, the fee
              agreed for them, and the agreement paper. Its nav entry carries
              nav.finance, and requiredNavKeyFor derives the route guard from
              ORG_NAV_ITEMS — so adding the entry is what gates this. */}
          <Route
            path="/org-admin/retainers"
            element={<AppRoute><OrgAdminRetainers /></AppRoute>}
          />
          <Route
            path="/org-admin/notifications"
            element={<AppRoute><OrgAdminBroadcast /></AppRoute>}
          />
          {/* Firm-level: which Tally companies are clients yet. Binding ONE
              client to ONE company stays on that client's own Tally tab — the
              two answer different questions. */}
          <Route
            path="/org-admin/tally"
            element={<AppRoute><OrgAdminTally /></AppRoute>}
          />
          <Route
            path="/org-admin/client-requests"
            element={<AppRoute><OrgAdminClientRequests /></AppRoute>}
          />
          <Route
            path="/org-admin/client-requests/new"
            element={<AppRoute><OrgAdminCreateClientRequest /></AppRoute>}
          />
          <Route
            path="/org-admin/client-requests/:id"
            element={<AppRoute><OrgAdminClientRequestDetail /></AppRoute>}
          />
          <Route
            path="/org-admin/clients/create"
            element={<AppRoute><CreateEditClient /></AppRoute>}
          />
          <Route
            path="/org-admin/clients/:id"
            element={<AppRoute requiredPermission="clients.view-profile"><ClientDetail /></AppRoute>}
          />
          <Route
            path="/org-admin/clients/:id/edit"
            element={<AppRoute><CreateEditClient /></AppRoute>}
          />
          <Route
            path="/org-admin/credentials"
            element={<AppRoute><OrgAdminCredentials /></AppRoute>}
          />
          <Route
            path="/org-admin/reports"
            element={<AppRoute><OrgAdminReports /></AppRoute>}
          />
          {/* Gated by `nav.audit-logs` — enforced on the API in BOTH services,
              so the route guard here is convenience, not the boundary. */}
          <Route
            path="/org-admin/audit-logs"
            element={<AppRoute><OrgAdminAuditLogs /></AppRoute>}
          />
          <Route
            path="/org-admin/documents"
            element={<AppRoute><OrgAdminDocuments /></AppRoute>}
          />
          <Route
            path="/org-admin/notices"
            element={<AppRoute><OrgAdminNotices /></AppRoute>}
          />
          {/* Gated by `client-notes.access` — the nav entry declares that key
              rather than a `nav.*` one, so `requiredNavKeyFor` derives the
              route guard from it automatically. The API router enforces the
              same key, which is the actual boundary. */}
          <Route
            path="/org-admin/notes"
            element={<AppRoute><OrgAdminNotes /></AppRoute>}
          />
          {/* The screen was "Client Notes" until notes stopped needing a
              client. The old path is kept as a redirect so bookmarks and
              anything that linked to it still land on the screen. */}
          <Route
            path="/org-admin/client-notes"
            element={<Navigate to="/org-admin/notes" replace />}
          />
          <Route
            path="/org-admin/dsc"
            element={<AppRoute><OrgAdminDSC /></AppRoute>}
          />
          <Route
            path="/org-admin/physical-files"
            element={<AppRoute><OrgAdminPhysicalFiles /></AppRoute>}
          />
          <Route
            path="/org-admin/approvals"
            element={<AppRoute><ApprovalQueue status="proposed" /></AppRoute>}
          />
          <Route
            path="/org-admin/approvals/approved"
            element={<AppRoute><ApprovalQueue status="approved" /></AppRoute>}
          />
          <Route
            path="/org-admin/approvals/rejected"
            element={<AppRoute><ApprovalQueue status="rejected" /></AppRoute>}
          />
          <Route
            path="/org-admin/compliance-calendar"
            element={<AppRoute><ComplianceDashboard /></AppRoute>}
          />
          <Route
            path="/org-admin/permissions"
            element={<AppRoute><OrgAdminPermissions /></AppRoute>}
          />

          {/* Employee area (staff below Partner). Merged from the former
              standalone employee portal; shares this app's auth + design system. */}
          {/* The bell's full history — search, filters, the lot.
              ⚠️ NOT UNDER /org-admin AND CARRYING NO NAV ENTRY. It is the
              caller's own feed, not a section of the firm, so `requiredNavKeyFor`
              answers null and lets it through — which is correct here and only
              here: the API has no endpoint that can read anybody else's rows, so
              there is no section-level question to ask. The page picks the
              cockpit or employee shell from the USER, the rule buildNavChrome
              already follows, which is why one route serves both.
              ⚠️ Not `/org-admin/notifications` — that is the broadcast composer,
              which is a different thing entirely (writing everyone else's). */}
          <Route path="/notifications" element={<AppRoute><Notifications /></AppRoute>} />

          <Route path="/employee" element={<AppRoute><EmployeeHome /></AppRoute>} />
          <Route path="/employee/tasks" element={<AppRoute><EmployeeTasks /></AppRoute>} />
          <Route path="/employee/todos" element={<AppRoute><EmployeeTodos /></AppRoute>} />
          <Route path="/employee/chat" element={<AppRoute><EmployeeChat /></AppRoute>} />
          <Route path="/employee/attendance" element={<AppRoute><EmployeeAttendance /></AppRoute>} />
          <Route path="/employee/profile" element={<AppRoute><EmployeeProfile /></AppRoute>} />
          {/* Shared task/compliance screens the employee area links to. `create`
              is declared before `:id` so the static path wins. */}
          <Route path="/tasks/create" element={<AppRoute><EmployeeCreateTask /></AppRoute>} />
          <Route path="/tasks/:id" element={<AppRoute><EmployeeTaskDetail /></AppRoute>} />
          <Route path="/compliance" element={<AppRoute><EmployeeCompliance /></AppRoute>} />

          {/* ⚠️ LAST, and it must stay last — a catch-all declared above any
              route swallows it. There was none at all before, so an unknown
              staff URL fell through every branch and rendered a BLANK PAGE,
              which reads as a crash rather than as a wrong address. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
       </BootGate>
       </NotificationProvider>
      </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
